import { useState } from 'react';
import type { PractitionerTimeOff, TimeOffReason } from '@salonomy/types';
import { Button, Card, Field, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import { useCreateTimeOff, useDeleteTimeOff, useTimeOff } from '../../data/practitioner.js';
import { longDate, todayInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS11 — Practitioner-level time off (brief 11 non-negotiable: "sick is sick everywhere"). A REAL,
 * usable surface (NOT design-gated) modelled on the scheduling TimeOffSection vocabulary, but operating
 * one level up: this time off is GLOBAL to the human and spans every salon they work in plus their own
 * private practice. The owning user is ALWAYS the session (the API never accepts a userId), so there is
 * no staff picker here — it is "you, everywhere".
 *
 * HONESTY: the cross-tenant double-booking guard reads this list on the server; we only display and
 * mutate it. No availability or conflict math happens on the client.
 */

const REASON_LABEL: Record<TimeOffReason, string> = {
  holiday: 'Holiday',
  sick: 'Sick',
  training: 'Training',
  other: 'Time off',
};
const REASONS = Object.entries(REASON_LABEL) as Array<[TimeOffReason, string]>;

export function PractitionerTimeOffPanel({ tz }: { tz: string }) {
  const toast = useToast();
  const { data: entries = [], isLoading } = useTimeOff();
  const create = useCreateTimeOff();
  const remove = useDeleteTimeOff();

  const today = todayInZone(tz);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState<TimeOffReason>('holiday');
  const [note, setNote] = useState('');

  function reset() {
    setStartDate(today);
    setEndDate(today);
    setReason('holiday');
    setNote('');
  }

  async function add() {
    if (endDate < startDate) {
      toast.error('End date can’t be before the start date.');
      return;
    }
    try {
      await create.mutateAsync({
        startDate,
        endDate,
        reason,
        note: note.trim() || null,
      });
      toast.success('Time off booked across every salon you work in');
      reset();
      setOpen(false);
    } catch {
      toast.error('Could not book this time off.');
    }
  }

  async function del(t: PractitionerTimeOff) {
    try {
      await remove.mutateAsync(t.id);
      toast.success('Time off removed');
    } catch {
      toast.error('Could not remove this time off.');
    }
  }

  const sorted = [...entries].sort((a, b) => a.startDate.localeCompare(b.startDate));

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="section-title">Your time off</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, maxWidth: 520 }}>
            This applies across <strong>every salon you work in</strong> and your own private
            practice — sick is sick everywhere. Booking it here blocks new appointments wherever you
            are.
          </div>
        </div>
        <Button
          size="sm"
          variant={open ? 'outline' : 'soft'}
          onClick={() => {
            reset();
            setOpen((v) => !v);
          }}
        >
          {open ? 'Cancel' : 'Add time off'}
        </Button>
      </div>

      {open ? (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="From">
              <TextInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="To">
              <TextInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Reason">
            <Select value={reason} onChange={(e) => setReason(e.target.value as TimeOffReason)}>
              {REASONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Note">
            <TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — for your own reference"
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Button onClick={add} disabled={create.isPending}>
              Book time off
            </Button>
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Loading your time off…
        </div>
      ) : sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          No time off booked.
        </div>
      ) : (
        <div className="settings-list">
          {sorted.map((t) => (
            <div key={t.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {t.startDate === t.endDate
                    ? longDate(t.startDate, tz)
                    : `${longDate(t.startDate, tz)} → ${longDate(t.endDate, tz)}`}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  <Pill tone="soft">{REASON_LABEL[t.reason]}</Pill>
                  {t.note ? ` · ${t.note}` : ''}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(t)} disabled={remove.isPending}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
